<template>
  <div class="page-shell backtest-forecast">
    <!-- 配置选择 + 刷新 -->
    <div class="toolbar">
      <span class="toolbar-label">预测配置</span>
      <el-select v-model="tag" class="toolbar-select" size="small" @change="load">
        <el-option-group v-for="g in groups" :key="g.key" :label="g.label">
          <el-option
            v-for="item in g.items"
            :key="item.tag"
            :value="item.tag"
            :label="optionLabel(item)"
          />
        </el-option-group>
      </el-select>
      <el-checkbox v-if="archiveCount" v-model="showArchive" class="toolbar-archive" size="mini">
        显示归档（{{ archiveCount }}）
      </el-checkbox>
      <span v-if="data" class="meta">更新于 {{ generatedAtText }}</span>
      <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="load">刷新</el-button>
    </div>

    <div v-if="error" class="error-bar">
      <el-alert :title="error" type="warning" :closable="false" show-icon />
    </div>

    <template v-if="data">
      <div v-if="!currentSignal" class="error-bar">
        <el-alert title="该配置暂无当期信号数据（需重新生成滚动结果）" type="info" :closable="false" show-icon />
      </div>

      <template v-else>
        <!-- 统计卡片 -->
        <div class="stat-cards">
          <div class="stat-card">
            <div class="stat-label">信号日</div>
            <div class="stat-value">{{ currentSignal.signalDate }}</div>
            <div class="stat-sub">数据截至 {{ currentSignal.asOf }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">持有窗口</div>
            <div class="stat-value stat-value-sm">{{ currentSignal.holdingFrom }} ~ {{ currentSignal.exitDate }}<template v-if="currentSignal.exitEstimated">（预计）</template></div>
            <div class="stat-sub">按最新收盘重算，供本周调仓参考</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">策略口径</div>
            <div class="stat-value stat-value-sm">{{ strategyText }}</div>
            <div class="stat-sub">Top-N 等权 · 周（持有 {{ config.hold }} 日）调仓</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">调仓统计</div>
            <div class="stat-value">
              <span class="txt-up">{{ currentSignal.turnover.buy }}</span>
              <span class="turnover-sep">/</span>
              {{ currentSignal.turnover.hold }}
              <span class="turnover-sep">/</span>
              <span class="txt-down">{{ currentSignal.turnover.sell }}</span>
            </div>
            <div class="stat-sub">新买 / 续持 / 调出 · 持仓 {{ currentSignal.picks.length }} 只（等权）</div>
          </div>
          <div v-if="currentSignal.stats" class="stat-card stat-card-wide">
            <div class="stat-label">历史参考统计（非预测）</div>
            <div class="stat-value stat-value-sm">
              {{ currentSignal.stats.n }} 期同口径平均
              <b :class="currentSignal.stats.avgRet >= 0 ? 'txt-up' : 'txt-down'">{{ pct(currentSignal.stats.avgRet, 2) }}</b>
              · 胜率 {{ pct(currentSignal.stats.winRate, 1) }}
              · 最佳 {{ pct(currentSignal.stats.best, 2) }}
              · 最差 {{ pct(currentSignal.stats.worst, 2) }}
            </div>
            <div class="stat-sub">过去各期 Top-N 组合在相同持有窗口的收益分布（已扣{{ costText }}），历史统计，非预测</div>
          </div>
        </div>

        <!-- 当期信号：最新周界按收盘因子重算的 Top-N（持有窗口未走完，不依赖未来收益） -->
        <div class="block signal-block">
          <div class="block-title">
            当期持仓明细（按因子值降序）
            <span class="caliber-hint">前 10 名 = 实盘口径（单票 ≤10%）；全部 = 研究口径（等权 ≈5.6%，仅研究用）</span>
          </div>
          <el-alert
            v-if="currentSignal.gateOff"
            title="状态闸门关闭：本期空仓（不满足开仓条件）"
            type="info"
            :closable="false"
            show-icon
          />
          <template v-else>
            <el-table :data="currentSignal.picks" size="mini" border stripe max-height="360" :row-class-name="picksRowClass">
              <el-table-column label="#" width="50">
                <template slot-scope="{ $index }">{{ $index + 1 }}</template>
              </el-table-column>
              <el-table-column label="口径" width="86">
                <template slot-scope="{ $index }">
                  <el-tag v-if="$index < liveTop" type="danger" size="mini">实盘</el-tag>
                  <el-tag v-else type="info" size="mini">研究</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="code" label="代码" width="90">
                <template slot-scope="{ row }">
                  <el-link
                    :href="stockstarCodeLink(row.code)"
                    target="_blank"
                    rel="noopener noreferrer"
                  >{{ row.code }}</el-link>
                </template>
              </el-table-column>
              <el-table-column prop="name" label="名称" min-width="110">
                <template slot-scope="{ row }">
                  <el-link
                    :href="eastmoneyCodeLink(row)"
                    target="_blank"
                    rel="noopener noreferrer"
                  >{{ row.name }}</el-link>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="86" align="center">
                <template slot-scope="{ row }">
                  <el-tag v-if="row.action === 'buy'" type="danger" size="mini">新买入</el-tag>
                  <el-tag v-else type="info" size="mini">继续持有</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="因子值" width="110" align="right">
                <template slot-scope="{ row }">
                  {{ formatFactorValue(config.factor, row.raw !== undefined ? row.raw : row.factor) }}
                  <div v-if="row.raw !== undefined && config.neutral === 'industry'" class="factor-pct">行业内 P{{ Math.round(row.factor * 100) }}</div>
                </template>
              </el-table-column>
            </el-table>
            <div v-if="currentSignal.sells && currentSignal.sells.length" class="sell-list">
              <span class="sell-title">本期调出：</span>
              <el-link
                v-for="s in currentSignal.sells"
                :key="s.code"
                :href="eastmoneyCodeLink(s)"
                target="_blank"
                rel="noopener noreferrer"
                class="sell-item"
              >{{ s.name }}</el-link>
            </div>
          </template>
          <div class="signal-note">历史统计，非预测 · 仅供参考，不构成投资建议</div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { fetchRollingBacktest } from '../utils/backtestApi.js'
import { formatFactorValue } from '../utils/factorFormat.js'
import { eastmoneyCodeLink, stockstarCodeLink } from '../utils/stockLink.js'

// 因子中文名（与 BacktestView 保持一致）
const FACTOR_LABELS = {
  r5: '近5日动量',
  r10: '近10日动量',
  rev5: '近5日反转',
  rev10: '近10日反转',
  vr: '量能比',
  mom60: '60日动量',
  mom120_20: '120日动量(剔近20日)',
  near_high: '52周高点距离',
  rsrs: 'RSRS趋势强度',
  sharpe60: '60日夏普动量',
  crash5: '近5日最大单日跌幅',
  crash10: '近10日最大单日跌幅',
  np_yoy: '净利同比增速',
  np_accel: '净利增速加速度',
  roe: '净资产收益率ROE',
  ep: '盈利收益率E/P',
  bp: '账面市值比B/P',
  cfq: '现金流质量倍数',
}

export default {
  name: 'BacktestForecast',
  data() {
    return {
      loading: false,
      error: '',
      tag: '',
      list: [],
      data: null,
      liveTop: 10, // 实盘口径持仓数（单票 ≤10%）；其余为研究口径
      showArchive: false, // 归档配置（已停维护 / 噪声因子 / 冗余变体）默认折叠
    }
  },
  computed: {
    config() {
      return this.data?.config || {}
    },
    currentSignal() {
      return this.data?.currentSignal || null
    },
    /** 下拉分组：核心监控 → 研究参考 →（可选）归档。tier 由服务层 listRollingSummary 给出 */
    groups() {
      const defs = [
        ['core', '核心监控'],
        ['research', '研究参考'],
        ['archive', '归档'],
      ]
      const out = []
      for (const [tier, name] of defs) {
        if (tier === 'archive' && !this.showArchive) continue
        const items = this.list.filter((i) => i.tier === tier)
        if (items.length) out.push({ key: tier, label: `${name}（${items.length}）`, items })
      }
      return out
    },
    archiveCount() {
      return this.list.filter((i) => i.tier === 'archive').length
    },
    strategyText() {
      const c = this.config
      if (!c.pool) return '—'
      const pool = c.pool === 'p75' ? 'P75 严格池' : 'Top500 池'
      const factor = this.factorLabel(c.factor)
      const neutral = c.neutral === 'industry' ? ' · 行业中性' : ''
      return `${pool} · ${factor}${neutral}`
    },
    generatedAtText() {
      const raw = this.data?.generatedAt
      if (!raw) return '—'
      return raw.replace('T', ' ').slice(0, 19)
    },
    /**
     * 交易成本口径文案。config.cost 来自归档 = 「一条腿一次完整买卖」（买 + 卖）的双边成本。
     * 不在此硬编码数值：口径调整后归档重跑即自动跟随。
     */
    costText() {
      const c = this.config.cost
      if (typeof c !== 'number') return '双边成本'
      const wan = c * 10000
      return `双边成本 万${Number.isInteger(wan) ? wan : wan.toFixed(1)}`
    },
  },
  watch: {
    // 取消「显示归档」时，若当前选中的正是归档配置 → 回退到核心首个，避免选中项从分组里消失
    showArchive(v) {
      if (v) return
      const cur = this.list.find((i) => i.tag === this.tag)
      if (!cur || cur.tier !== 'archive') return
      const first = this.list.find((i) => i.tier === 'core') || this.list[0]
      if (first) {
        this.tag = first.tag
        this.load()
      }
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    optionLabel(item) {
      return `${item.tag} · 信号 ${item.latestSignalDate || '—'} · 超额 ${this.pct(item.cumExcess, 1)}`
    },
    /** 因子中文名；合成因子（如 r10+rsrs）逐段翻译后用 + 连接 */
    factorLabel(factor) {
      if (!factor) return '—'
      return String(factor)
        .split('+')
        .map((f) => FACTOR_LABELS[f] || f)
        .join(' + ')
    },
    picksRowClass({ rowIndex }) {
      return rowIndex < this.liveTop ? 'picks-live-row' : ''
    },
    async load() {
      this.loading = true
      this.error = ''
      try {
        const res = await fetchRollingBacktest(this.tag)
        // 只保留有当期信号数据的配置（旧版 rolling json 无 currentSignal，不展示）
        const signalList = (res.list || []).filter((i) => i.hasSignal !== false)
        this.list = signalList
        this.tag = res.tag
        this.data = res.data
        // 当前选中配置无当期信号（如手动传入旧 tag）→ 自动切到列表第一个有信号的配置
        if (!this.data?.currentSignal && signalList.length) {
          const first = signalList[0]
          if (first && first.tag !== this.tag) {
            const res2 = await fetchRollingBacktest(first.tag)
            this.tag = res2.tag
            this.data = res2.data
          }
        }
      } catch (err) {
        this.error = err.message || '加载当期信号失败'
      } finally {
        this.loading = false
      }
    },
    pct(v, digits = 2) {
      return v === null || v === undefined ? '—' : `${(v * 100).toFixed(digits)}%`
    },
    num(v, digits = 2) {
      return v === null || v === undefined ? '—' : Number(v).toFixed(digits)
    },
    // 因子值按量纲展示（% / 万% / 比率），仅影响可读性、不改变排序
    formatFactorValue,
    // 代码跳证券之星、名称跳东方财富（与基金重仓/波段雷达等页面保持一致）
    eastmoneyCodeLink,
    stockstarCodeLink,
  },
}
</script>

<style scoped>
.backtest-forecast {
  padding: 4px;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.toolbar-label {
  font-weight: 600;
  color: #2c3e50;
  flex: 0 0 auto;
  white-space: nowrap;
}

.toolbar-select {
  width: 300px;
}

.toolbar-archive {
  flex: 0 0 auto;
}

.toolbar-archive ::v-deep .el-checkbox__label {
  font-size: 12px;
  color: #909399;
}

.meta {
  color: #909399;
  font-size: 12px;
}

.error-bar {
  margin-bottom: 14px;
}

.stat-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.stat-card {
  background: #f8f9fb;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 12px 14px;
}

.stat-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #303133;
}

.stat-value-sm {
  font-size: 15px;
  line-height: 1.4;
}

.stat-sub {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

.turnover-sep {
  color: #c0c4cc;
  font-weight: 400;
  margin: 0 2px;
}

.block {
  margin-bottom: 18px;
}

.block-title {
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}

.caliber-hint {
  font-size: 12px;
  font-weight: 400;
  color: #909399;
  margin-left: 10px;
}

.factor-pct {
  font-size: 11px;
  color: #909399;
  line-height: 1.2;
}

::v-deep .el-table .picks-live-row {
  background: #fdf6ec;
}

::v-deep .el-table .picks-live-row td {
  border-bottom: 1px solid #f5dab1;
}

/* 当期信号卡片 */
.signal-block {
  padding: 12px 14px;
  border: 1px solid #f5dab1;
  border-radius: 6px;
  background: #fdfcf8;
}

.sell-list {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.8;
}

.sell-title {
  color: #606266;
}

.sell-item {
  margin-right: 10px;
  font-size: 12px;
}

.signal-note {
  margin-top: 8px;
  font-size: 12px;
  color: #c0c4cc;
}

.txt-up {
  color: #f56c6c;
  font-weight: 600;
}

.txt-down {
  color: #67c238;
  font-weight: 600;
}

/* 移动端（断点与 utils/device.js 的 768px 保持一致）：
   工具栏第一行 = 配置 + 下拉 + 刷新，「更新于 xxx」独占第二行小字 */
@media (max-width: 768px) {
  .toolbar {
    flex-wrap: wrap;
    gap: 8px;
    row-gap: 4px;
    margin-bottom: 10px;
  }

  /* 下拉吃掉第一行剩余宽度 */
  .toolbar-select {
    flex: 1 1 0;
    width: auto;
    min-width: 0;
  }

  /* 归档开关换到第二行（字号收小，不与下拉抢宽度） */
  .toolbar-archive {
    order: 2;
  }

  .toolbar-archive ::v-deep .el-checkbox__label {
    font-size: 11px;
  }

  /* 更新时间独占第二行：不压缩下拉，也不消失 */
  .meta {
    order: 3;
    flex: 1 1 100%;
    font-size: 11px;
  }

  .stat-cards {
    gap: 8px;
    margin-bottom: 10px;
  }

  .stat-card {
    padding: 8px 12px;
  }

  .stat-label {
    margin-bottom: 2px;
  }

  .stat-value {
    font-size: 18px;
  }

  /* 窄屏卡片仅约 165~190px：允许折行，避免内容被 nowrap 裁掉（如持有窗口的「（预计）」） */
  .stat-value-sm {
    font-size: 14px;
    line-height: 1.45;
    white-space: normal;
  }

  /* 「历史参考统计」整行卡的四段数字较长 → 字号略收，行距放宽 */
  .stat-card-wide .stat-value-sm {
    font-size: 13px;
    line-height: 1.6;
  }

  .stat-sub {
    margin-top: 2px;
  }

  .stat-card-wide {
    grid-column: 1 / -1;
  }

  .signal-block {
    padding: 8px 12px;
    margin-bottom: 10px;
  }
}
</style>
